import HdButton from "@hd/ui-kit/hd-button/HdButton";
import HdModal from "@hd/ui-kit/hd-modal/HdModal";
import { t } from "i18next";
import { ReactNode, useCallback, useRef, useState } from "react";
import { ConfirmContext, ConfirmOptions } from "./confirmContext";

// Default options
const defaultOptions: ConfirmOptions = {
  title: t("common.confirmDefaultTitle"),
  description: t("common.confirmDefaultText"),
  confirmLabel: t("common.confirm"),
  cancelLabel: t("common.cancel"),
  variant: "info",
};

export const ConfirmModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>(defaultOptions);

  // Usiamo un ref per mantenere la funzione "resolve" della Promise
  // Questo ci permette di chiamarla quando l'utente clicca un bottone
  const resolveRef = useRef<(value: boolean) => void>(() => {});

  const confirm = useCallback((options?: ConfirmOptions) => {
    setOptions({ ...defaultOptions, ...options });
    setIsOpen(true);

    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    // Se si chiude (es. click fuori o ESC), risolviamo come false
    resolveRef.current(false);
  };

  const handleConfirm = () => {
    setIsOpen(false);
    resolveRef.current(true);
  };

  const handleCancel = () => {
    setIsOpen(false);
    resolveRef.current(false);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      <HdModal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        zIndex={40}
        backdrop
        closeOnEsc
        closeOnClickOutside
        className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl border border-gray-100" // Stili base del pannello
      >
        <div className="flex flex-col gap-4">
          {/* Header */}
          <h2 className="text-md font-semibold text-gray-900">
            {options.title}
          </h2>

          {/* Body */}
          <p className="text-sm text-bodyText h-full">{options.description}</p>

          {/* Footer / Actions */}
          <div className="flex justify-end gap-3 mt-4">
            <HdButton
              variant="gray"
              type="button"
              onClick={(e) => {
                //preventDefault e stopPropagation per evitare che se ci sono altre modali aperte sotto a questa, vengano chiuse tutte con clickOutside dell' HdOffcanvas
                e.preventDefault();
                e.stopPropagation();
                handleCancel();
              }}
            >
              {options.cancelLabel}
            </HdButton>
            <HdButton
              onClick={(e) => {
                //preventDefault e stopPropagation per evitare che se ci sono altre modali aperte sotto a questa, vengano chiuse tutte con clickOutside dell' HdOffcanvas
                e.preventDefault();
                e.stopPropagation();
                handleConfirm();
              }}
              variant="primary"
              type="button"
            >
              {options.confirmLabel}
            </HdButton>
          </div>
        </div>
      </HdModal>
    </ConfirmContext.Provider>
  );
};
